import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * CDN and regional distribution (part 59).
 *
 * The page does not display a hit rate anybody typed in. Pressing "วัดแคช" makes the API issue N
 * real requests to the live Worker and read the x-edge-cache header on every answer; the counts,
 * the latency of hits versus misses and the POP breakdown below are what those requests returned.
 *
 * Two modes exist for a reason. "วัดตามปกติ" measures a key as a user would meet it, so a warm key
 * can hit. "วัดแบบตัดแคช" appends a unique query string to every request, so a hit would be a bug
 * and the run is stored with zero hits by constraint. A cold result from the second mode is
 * evidence the key includes the query string; it is not evidence that the cache is broken.
 *
 * The purge card only plans. It validates tags, URLs and prefixes, groups them into the batches a
 * purge API would receive and shows which configured routes the tags address. Nothing is deleted
 * from this screen.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 */

type CacheStatus = 'hit' | 'miss' | 'bypass' | 'error'

interface CDNRoute {
  id: string
  method: string
  path_pattern: string
  match_type: 'exact' | 'prefix'
  ttl_class: string
  ttl_seconds: number
  stale_while_revalidate: number
  vary_headers: string[]
  tags: string[]
  enabled: boolean
  note_th: string
  created_at: string
  updated_at: string
}

interface Observation {
  cache: CacheStatus
  status: number
  duration_ms: number
  pop: string
  region: string
  content_type?: string
}

interface POPStat {
  pop: string
  region: string
  requests: number
  hits: number
  misses: number
  weight: number
}

interface Decision {
  cacheable: boolean
  ttl_class: string
  ttl_seconds: number
  stale_while_revalidate: number
  vary: string[]
  reason_th: string
}

interface Measurement {
  id: string
  route_id?: string
  route_path?: string
  route_ttl_class?: string
  target_url: string
  mode: 'normal' | 'busted'
  requests: number
  hits: number
  misses: number
  bypasses: number
  errors: number
  hit_ratio: number
  weighted_hit_ratio: number
  avg_hit_ms: number
  avg_miss_ms: number
  pop: string
  pop_region: string
  verdict: string
  observations: Observation[]
  measured_by_role: string
  duration_ms: number
  created_at: string
}

interface MeasureResult {
  measurement: Measurement
  summary: {
    requests: number
    hits: number
    misses: number
    bypasses: number
    errors: number
    hit_ratio: number
    weighted_hit_ratio: number
    avg_hit_ms: number
    avg_miss_ms: number
    saved_ms: number
    pop: string
    pop_region: string
    verdict: string
    summary_th: string
    pops: POPStat[]
  }
  observations: Observation[]
  decision: Decision
  route?: CDNRoute
  pops: POPStat[]
}

interface PolicyClass {
  class: string
  label_th: string
  min_ttl: number
  max_ttl: number
  default_ttl: number
}

interface Policy {
  classes: PolicyClass[]
  min_requests: number
  max_requests: number
  max_tags: number
  max_urls: number
  max_prefixes: number
  tags_per_batch: number
  urls_per_batch: number
  target_origin: string
  measure_timeout: number
  modes: { mode: string; label_th: string }[]
}

interface PurgePlan {
  tenant_id: string
  tags: string[]
  urls: string[]
  prefixes: string[]
  batches: { kind: string; values: string[] }[]
  requests: number
  warnings: string[]
  summary_th: string
}

interface PurgeResult {
  plan: PurgePlan
  routes_matched: CDNRoute[]
  routes_total: number
  executed: boolean
  note_th: string
}

const TTL_LABEL: Record<string, string> = {
  static: 'ไฟล์คงที่',
  api_short: 'ข้อมูลรวมอายุสั้น',
  api_medium: 'ข้อมูลอ้างอิง',
  no_store: 'ห้ามแคช',
}

const CACHE_LABEL: Record<CacheStatus, string> = {
  hit: 'แคชตอบ',
  miss: 'พลาดแคช',
  bypass: 'ข้ามแคช',
  error: 'ผิดพลาด',
}

const CACHE_TONE: Record<CacheStatus, string> = {
  hit: 'var(--color-emerald-soft)',
  miss: 'var(--color-gold)',
  bypass: 'var(--color-ivory-muted)',
  error: 'var(--color-crimson)',
}

const VERDICT_LABEL: Record<string, string> = {
  healthy: 'แคชทำงานดี',
  partial: 'แคชทำงานบางส่วน',
  cold: 'ยังไม่เกิดแคช',
  bypass: 'ไม่ได้ผ่านแคช',
  broken: 'แคชผิดพลาด',
}

const VERDICT_TONE: Record<string, string> = {
  healthy: 'var(--color-emerald-soft)',
  partial: 'var(--color-gold)',
  cold: 'var(--color-amber)',
  bypass: 'var(--color-ivory-muted)',
  broken: 'var(--color-crimson)',
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function ms(value: number): string {
  return value < 0 ? '—' : `${value} ms`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function regionLabel(region: string): string {
  if (region === 'apac') return 'เอเชียแปซิฟิก'
  if (region === 'eu') return 'ยุโรป'
  if (region === 'us') return 'อเมริกาเหนือ'
  return 'อื่น ๆ'
}

function splitTags(raw: string): string[] {
  return raw.split(/[,\s]+/).map((tag) => tag.trim()).filter(Boolean)
}

function splitLines(raw: string): string[] {
  return raw.split(/\n+/).map((line) => line.trim()).filter(Boolean)
}

export function CDN() {
  const { me } = useAuth()
  const canMeasure = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const [sample, setSample] = useState(6)
  const [url, setUrl] = useState('')
  const [notice, setNotice] = useState('')
  const [result, setResult] = useState<MeasureResult | null>(null)
  const [mode, setMode] = useState<'normal' | 'busted'>('normal')

  const [purgeTags, setPurgeTags] = useState('dashboard,summary')
  const [purgePrefixes, setPurgePrefixes] = useState('/assets/')
  const [purgeURLs, setPurgeURLs] = useState('')
  const [purge, setPurge] = useState<PurgeResult | null>(null)

  const policy = useQuery({
    queryKey: ['cdn-policy'],
    queryFn: () => api<Policy>('/v1/cdn/policy'),
    staleTime: Infinity,
  })

  const routes = useQuery({
    queryKey: ['cdn-routes'],
    queryFn: () => api<CDNRoute[]>('/v1/cdn/routes'),
  })

  const measurements = useQuery({
    queryKey: ['cdn-measurements'],
    queryFn: () => api<Measurement[]>('/v1/cdn/measurements?limit=30'),
  })

  const targetOrigin = policy.data?.target_origin ?? 'https://auditai-edge.toojinda2.workers.dev'
  const effectiveURL = url || `${targetOrigin}/v1/board/summary`

  const measure = useMutation({
    mutationFn: (vars: { mode: 'normal' | 'busted' }) =>
      apiWrite<MeasureResult>('/v1/cdn/measurements', 'POST', {
        url: effectiveURL,
        requests: sample,
        mode: vars.mode,
      }),
    onSuccess: (saved) => {
      setResult(saved)
      setMode(saved.measurement.mode)
      setNotice(`${saved.summary.summary_th} · ใช้เวลา ${saved.measurement.duration_ms} ms`)
      void qc.invalidateQueries({ queryKey: ['cdn-measurements'] })
    },
    onError: (err) => setNotice(errorText(err, 'วัดแคชไม่สำเร็จ')),
  })

  const planPurge = useMutation({
    mutationFn: () =>
      apiWrite<PurgeResult>('/v1/cdn/purge-plan', 'POST', {
        tags: splitTags(purgeTags),
        urls: splitLines(purgeURLs),
        prefixes: splitTags(purgePrefixes),
      }),
    onSuccess: (saved) => {
      setPurge(saved)
      setNotice(saved.plan.summary_th)
    },
    onError: (err) => setNotice(errorText(err, 'วางแผนล้างแคชไม่สำเร็จ')),
  })

  const routeRows = routes.data ?? []
  const enabledRoutes = routeRows.filter((route) => route.enabled).length
  const latest = result?.measurement ?? measurements.data?.[0] ?? null
  const latestSummary = result?.summary ?? null
  const pops = result?.pops ?? latestSummary?.pops ?? []

  const classHint = useMemo(() => {
    const map: Record<string, PolicyClass> = {}
    for (const row of policy.data?.classes ?? []) map[row.class] = row
    return map
  }, [policy.data])

  return (
    <>
      <PageHead
        title="CDN และการกระจายภูมิภาค"
        sub="วัดแคชจากคำขอจริงไปยัง Worker ที่ติดตั้งอยู่ · นับจากหัว x-edge-cache · แยกตาม POP ที่ตอบ · ไม่มีการประมาณค่าฮิต"
        actions={canMeasure ? (
          <div className="row" style={{ gap: 8 }}>
            <button
              className={mode === 'normal' ? 'btn btn-primary' : 'btn'}
              onClick={() => { setMode('normal'); setNotice(''); measure.mutate({ mode: 'normal' }) }}
              disabled={measure.isPending}
            >
              {measure.isPending && mode === 'normal' ? 'กำลังวัด…' : 'วัดตามปกติ'}
            </button>
            <button
              className={mode === 'busted' ? 'btn btn-primary' : 'btn'}
              onClick={() => { setMode('busted'); setNotice(''); measure.mutate({ mode: 'busted' }) }}
              disabled={measure.isPending}
            >
              {measure.isPending && mode === 'busted' ? 'กำลังวัด…' : 'วัดแบบตัดแคช'}
            </button>
          </div>
        ) : undefined}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip chip-on">ต้นทาง {targetOrigin}</span>
        <span className="chip">เส้นทางทั้งหมด {routeRows.length} · เปิดใช้ {enabledRoutes}</span>
        <span className="chip">ตัวอย่างสูงสุด {policy.data?.max_requests ?? 12} คำขอ</span>
        <span className="chip">แท็กต่อคำขอ {policy.data?.tags_per_batch ?? 30}</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {latest ? (
        <div className="grid-4" style={{ marginBottom: 16 }}>
          <Stat
            label="อัตราการตอบจากแคช"
            value={<span className="tnum" style={{ color: VERDICT_TONE[latest.verdict] ?? undefined }}>{pct(latest.hit_ratio)}</span>}
            foot={`${VERDICT_LABEL[latest.verdict] ?? latest.verdict} · ${latest.mode === 'busted' ? 'โหมดตัดแคช' : 'โหมดปกติ'}`}
          />
          <Stat
            label="คำขอที่นับได้"
            value={latest.requests}
            unit="คำขอ"
            foot={`แคชตอบ ${latest.hits} · พลาด ${latest.misses} · ข้าม ${latest.bypasses} · ผิดพลาด ${latest.errors}`}
          />
          <Stat
            label="เวลาแคชตอบ"
            value={ms(latest.avg_hit_ms)}
            foot={`พลาดแคช ${ms(latest.avg_miss_ms)} · ถ่วงน้ำหนักตาม POP ${pct(latest.weighted_hit_ratio)}`}
          />
          <Stat
            label="POP ที่ตอบ"
            value={latest.pop || '—'}
            foot={`${regionLabel(latest.pop_region)} · ${thaiDateTime(latest.created_at)}`}
          />
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div className="card-label">วัดแคชจากโฮสต์จริง</div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ต้องมีสิทธิ์ owner, admin หรือ audit_manager · ระบบส่งคำขอ N ครั้งตามลำดับเพื่อให้เห็นทั้งจังหวะที่พลาดและตอบจากแคช
          </span>
        </div>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 420px', minWidth: 280 }}>
            <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>URL ที่วัด (ต้องอยู่บนต้นทางเดียวกัน)</div>
            <input className="input" value={effectiveURL} onChange={(event) => setUrl(event.target.value)}
              spellCheck={false} aria-label="URL ที่วัด" />
          </label>
          <label>
            <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>จำนวนคำขอ</div>
            <select className="input" style={{ width: 120 }} value={sample} aria-label="จำนวนคำขอ"
              onChange={(event) => setSample(Number(event.target.value))}>
              {[3, 6, 10, 12].map((value) => <option key={value} value={value}>{value} คำขอ</option>)}
            </select>
          </label>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
          โหมดตัดแคชเติมพารามิเตอร์ไม่ซ้ำในทุกคำขอ ทุกคำขอจึงต้องพลาด · ผลฮิตในโหมดนี้จะถูกปฏิเสธโดยฐานข้อมูล
        </div>
      </div>

      {measure.isPending ? (
        <div className="stack" style={{ marginBottom: 16 }}><Skel h={120} /><Skel h={220} /></div>
      ) : result ? (
        <>
          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="card-label">ผลการวัดล่าสุด</div>
                <div className="mono dim" style={{ fontSize: 11.5, marginTop: 4 }}>{result.measurement.target_url}</div>
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="chip" style={{ color: VERDICT_TONE[result.measurement.verdict] }}>
                  {VERDICT_LABEL[result.measurement.verdict] ?? result.measurement.verdict}
                </span>
                <span className="chip">{result.decision.cacheable ? 'เส้นทางนี้แคชได้' : 'เส้นทางนี้ไม่แคช'}</span>
                <span className="chip">{TTL_LABEL[result.decision.ttl_class] ?? result.decision.ttl_class}</span>
              </div>
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>
              เหตุผลตามนโยบาย: {result.decision.reason_th}
              {result.route ? ` · ตรงกับเส้นทาง ${result.route.path_pattern} (${result.route.match_type})` : ' · ไม่ตรงกับเส้นทางที่ตั้งค่าไว้'}
            </div>
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="card-label" style={{ marginBottom: 10 }}>หลักฐานรายคำขอ</div>
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th className="num">ครั้งที่</th>
                    <th>ผลแคช</th>
                    <th className="num">สถานะ</th>
                    <th className="num">เวลา</th>
                    <th>POP</th>
                    <th>ภูมิภาค</th>
                  </tr>
                </thead>
                <tbody>
                  {result.observations.map((row, index) => (
                    <tr key={`${index}-${row.pop}`}>
                      <td className="num tnum">{index + 1}</td>
                      <td>
                        <span className="sev" style={{ color: CACHE_TONE[row.cache] }}>
                          <i className="sev-dot" style={{ background: CACHE_TONE[row.cache] }} />
                          {CACHE_LABEL[row.cache] ?? row.cache}
                        </span>
                      </td>
                      <td className="num tnum">{row.status === 0 ? '—' : row.status}</td>
                      <td className="num tnum">{row.duration_ms} ms</td>
                      <td className="mono">{row.pop || '—'}</td>
                      <td className="dim" style={{ fontSize: 12 }}>{regionLabel(row.region)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pops.length > 0 ? (
            <div className="card card-pad" style={{ marginBottom: 16 }}>
              <div className="card-label" style={{ marginBottom: 10 }}>น้ำหนักตาม POP</div>
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>POP</th>
                      <th>ภูมิภาค</th>
                      <th className="num">คำขอ</th>
                      <th className="num">แคชตอบ</th>
                      <th className="num">อัตราฮิต</th>
                      <th className="num">น้ำหนัก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pops.map((row) => (
                      <tr key={row.pop}>
                        <td className="mono">{row.pop}</td>
                        <td className="dim" style={{ fontSize: 12 }}>{regionLabel(row.region)}</td>
                        <td className="num tnum">{row.requests}</td>
                        <td className="num tnum">{row.hits}</td>
                        <td className="num tnum">{pct(row.requests ? row.hits / row.requests : 0)}</td>
                        <td className="num tnum">{pct(row.weight)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                อัตราฮิตแบบถ่วงน้ำหนัก {pct(result.summary.weighted_hit_ratio)} · คำนวณจากสัดส่วนคำขอที่แต่ละ POP รับจริง
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="card-label" style={{ marginBottom: 10 }}>เส้นทางแคชที่ตั้งค่าไว้</div>
        {routes.isLoading ? (
          <Skel h={160} />
        ) : routeRows.length === 0 ? (
          <Empty title="ยังไม่มีเส้นทางแคช" body="ผู้ดูแลระบบต้องเพิ่มเส้นทางก่อน จึงจะวัดและล้างแคชได้" />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เส้นทาง</th>
                  <th>ประเภท</th>
                  <th>ชั้น TTL</th>
                  <th className="num">TTL</th>
                  <th className="num">SWR</th>
                  <th>แท็ก</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {routeRows.map((route) => (
                  <tr key={route.id}>
                    <td>
                      <div className="mono" style={{ fontSize: 12 }}>{route.method} {route.path_pattern}</div>
                      <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{route.note_th}</div>
                    </td>
                    <td className="dim" style={{ fontSize: 12 }}>{route.match_type === 'exact' ? 'ตรงทั้งเส้น' : 'ขึ้นต้นด้วย'}</td>
                    <td>
                      <span className="chip" style={{ color: route.ttl_class === 'no_store' ? 'var(--color-crimson)' : undefined }}>
                        {TTL_LABEL[route.ttl_class] ?? route.ttl_class}
                      </span>
                    </td>
                    <td className="num tnum">{route.ttl_seconds === 0 ? '—' : `${route.ttl_seconds} s`}</td>
                    <td className="num tnum">{route.stale_while_revalidate === 0 ? '—' : `${route.stale_while_revalidate} s`}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{route.tags.join(', ')}</td>
                    <td>
                      <span className="sev" style={{ color: route.enabled ? 'var(--color-emerald-soft)' : 'var(--color-ivory-muted)' }}>
                        <i className="sev-dot" style={{ background: route.enabled ? 'var(--color-emerald-soft)' : 'var(--color-ivory-muted)' }} />
                        {route.enabled ? 'เปิดใช้' : 'ปิด'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {policy.data ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {policy.data.classes.map((row) => (
              <span className="chip" key={row.class}>
                {row.label_th}: {row.min_ttl === 0 ? 'ไม่เก็บ' : `${row.min_ttl}–${row.max_ttl} วินาที`}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div className="card-label">วางแผนล้างแคชตามแท็ก</div>
          <span className="dim" style={{ fontSize: 11.5 }}>หน้านี้ไม่สั่งล้างจริง · แผนจะบอกจำนวนคำขอและเส้นทางที่ได้รับผล</span>
        </div>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 240px' }}>
            <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>แท็ก (คั่นด้วยจุลภาค)</div>
            <input className="input" value={purgeTags} onChange={(event) => setPurgeTags(event.target.value)}
              spellCheck={false} aria-label="แท็กที่ต้องการล้าง" />
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>คำนำหน้าเส้นทาง</div>
            <input className="input" value={purgePrefixes} onChange={(event) => setPurgePrefixes(event.target.value)}
              spellCheck={false} aria-label="คำนำหน้าเส้นทาง" />
          </label>
          <button className="btn" onClick={() => { setNotice(''); planPurge.mutate() }} disabled={planPurge.isPending || !canMeasure}>
            {planPurge.isPending ? 'กำลังวางแผน…' : 'วางแผนล้างแคช'}
          </button>
        </div>
        <label style={{ display: 'block', marginTop: 10 }}>
          <div className="dim" style={{ fontSize: 11.5, marginBottom: 6 }}>URL เจาะจง (บรรทัดละหนึ่งรายการ ไม่รับไวลด์การ์ด)</div>
          <textarea className="input" rows={2} value={purgeURLs} onChange={(event) => setPurgeURLs(event.target.value)}
            spellCheck={false} placeholder="https://auditai-edge.toojinda2.workers.dev/v1/board/summary"
            aria-label="URL ที่ต้องการล้าง" />
        </label>
        {!canMeasure ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>บัญชีของคุณอ่านได้อย่างเดียว ไม่สามารถวางแผนล้างแคชได้</div>
        ) : null}

        {purge ? (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--color-hairline)', paddingTop: 12 }}>
            <div style={{ fontSize: 13 }}>{purge.plan.summary_th}</div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {purge.plan.batches.map((batch, index) => (
                <span className="chip" key={`${batch.kind}-${index}`}>
                  {batch.kind === 'tag' ? 'แท็ก' : batch.kind === 'url' ? 'URL' : 'คำนำหน้า'} {batch.values.length} รายการ
                </span>
              ))}
              <span className="chip">คำขอทั้งหมด {purge.plan.requests}</span>
              <span className="chip">เส้นทางที่ตรง {purge.routes_matched.length}/{purge.routes_total}</span>
            </div>
            {purge.plan.warnings.length > 0 ? (
              <div className="stack" style={{ marginTop: 10 }}>
                {purge.plan.warnings.map((warning) => (
                  <div key={warning} className="dim" style={{ fontSize: 12, borderLeft: '3px solid var(--color-gold)', paddingLeft: 10 }}>
                    {warning}
                  </div>
                ))}
              </div>
            ) : null}
            {purge.routes_matched.length > 0 ? (
              <div className="dim mono" style={{ fontSize: 11.5, marginTop: 10 }}>
                {purge.routes_matched.map((route) => route.path_pattern).join(' · ')}
              </div>
            ) : null}
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>{purge.note_th}</div>
          </div>
        ) : null}
      </div>

      <div className="card card-pad">
        <div className="card-label" style={{ marginBottom: 10 }}>ประวัติการวัด</div>
        {measurements.isLoading ? (
          <Skel h={160} />
        ) : (measurements.data ?? []).length === 0 ? (
          <Empty title="ยังไม่มีการวัดแคช" body="กดวัดตามปกติเพื่อสร้างหลักฐานชุดแรกจาก Worker ที่ติดตั้งอยู่" />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>เส้นทาง</th>
                  <th>โหมด</th>
                  <th className="num">คำขอ</th>
                  <th className="num">ฮิต</th>
                  <th className="num">อัตราฮิต</th>
                  <th>ผล</th>
                  <th>POP</th>
                </tr>
              </thead>
              <tbody>
                {(measurements.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="dim" style={{ fontSize: 12 }}>{thaiDateTime(row.created_at)}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{row.route_path || row.target_url}</td>
                    <td className="dim" style={{ fontSize: 12 }}>{row.mode === 'busted' ? 'ตัดแคช' : 'ปกติ'}</td>
                    <td className="num tnum">{row.requests}</td>
                    <td className="num tnum">{row.hits}</td>
                    <td className="num tnum">{pct(row.hit_ratio)}</td>
                    <td>
                      <span className="sev" style={{ color: VERDICT_TONE[row.verdict] }}>
                        <i className="sev-dot" style={{ background: VERDICT_TONE[row.verdict] }} />
                        {VERDICT_LABEL[row.verdict] ?? row.verdict}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{row.pop || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {policy.data ? (
        <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
          ชั้น TTL ที่รองรับ: {policy.data.classes.map((row) => `${row.class} (${row.label_th})`).join(' · ')}
          {classHint.static ? ` · ค่าเริ่มต้นไฟล์คงที่ ${classHint.static.default_ttl} วินาที` : ''}
        </div>
      ) : null}
    </>
  )
}
